import * as React from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import WorldMap, { CountryInfo } from '../../../widgets/WorldMap';
import { BaseDataEntry } from './BaseDataView';

type MapProps<D extends BaseDataEntry> = {
  data: Array<D>;
  countField: string;
};

type DataCount = {
  min: number;
  max: number;
  range: number;
};

export default class DataMap<
  D extends BaseDataEntry,
> extends React.PureComponent<MapProps<D>> {
  /**
   * Returns the CSS hsl(...) colour function (as a string) for the map country colour
   * given the desired darkness amount parameter
   * @param {number} current - decimal darkness level between 0 and 1 where 1 is darkest
   * @param {DataCount} stats - count stats
   * @return {string} - hsl string to use directly in a "style" property
   */
  getColor = (current: number, stats: DataCount): string => {
    // Our desired colour range is from 37% for darkest to 60% for lightest
    // We simply subtract a percentage of the 34-point difference from 60%
    let lightness = 37; // start out at darkest
    if (stats.range) {
      lightness = 60 - (23 * (current - stats.min)) / stats.range;
    }
    return `hsl(208, 63%, ${lightness.toFixed(1)}%)`;
  };

  BASE_STATS = { min: 0, max: 1, range: 1 };
  DARKEST = this.getColor(1, this.BASE_STATS);
  LIGHTEST = this.getColor(0, this.BASE_STATS);

  render() {
    const { data, countField } = this.props;
    const countryInfo: {
      [key: string]: CountryInfo;
    } = {};
    let stats = { max: 0, min: Number.MAX_VALUE, range: 0 };
    data.forEach((row) => {
      stats.max = Math.max(stats.max, row[countField]);
      stats.min = Math.min(stats.min, row[countField]);
    });
    stats.range = stats.max - stats.min;

    data.forEach((row: D) => {
      const { name, code } = row;
      if (code) {
        countryInfo[code] = {
          tooltip: (
            <UncontrolledTooltip
              delay={0}
              key={code}
              target={`#${code}`}
              placement="right"
            >
              {name}
            </UncontrolledTooltip>
          ),
          color: this.getColor(row[countField], stats),
        };
      }
    });

    return (
      <div className="d-flex">
        <div className="d-flex flex-column align-self-end mt-3 flex-shrink-0">
          <div className="mb-3">More</div>
          <div
            style={{
              minHeight: '150px',
              background: `linear-gradient(${this.DARKEST},${this.LIGHTEST})`,
            }}
          />
          <div className="mt-3">Less</div>
        </div>
        <WorldMap
          id="data-map"
          className="flex-fill"
          countryInfo={countryInfo}
        />
      </div>
    );
  }
}
